import { DOCUMENT } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import type {
  SupportChatConfig,
  SupportChatConfigGetResponseAjaxApi,
} from './deskpro-chat-widget.model';
import type {
  DeskproChatWidgetElement,
  DeskproChatWidgetGetConfigResult,
  DeskproChatWidgetLoadWidgetScriptResult,
  DeskproChatWidgetOpenResult,
  DeskproChatWidgetReadyEvent,
} from './deskpro-chat-widget.types';

const WAITING_TIMEOUT_IN_SECONDS = 450;
const WIDGET_AUTH_ELEMENT_ID = 'dp-chat-widget-auth-element';
const WIDGET_SCRIPT_ID = 'dp-chat-widget-script';
const WIDGET_LOADER_SELECTOR = 'iframe[name="dp_loader"]';
const WIDGET_CONTAINER_SELECTOR = '#dp_widget_container';
const WIDGET_BUTTON_SELECTOR =
  '#dp_widget_container iframe[name=widget_trigger_iframe]';
const WIDGET_READY_EVENT = 'dpwidget.ready';

@UntilDestroy({ className: 'DeskproChatWidget' })
@Injectable({
  providedIn: 'root',
})
export class DeskproChatWidget implements OnDestroy {
  private onWidgetElementReady: (event: Event) => void;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private errorHandler: ErrorHandler,
    private freelancerHttp: FreelancerHttp,
  ) {}

  ngOnDestroy(): void {
    this.clearElements();
  }

  async open(): Promise<DeskproChatWidgetOpenResult> {
    this.clearElements();

    const getConfigResult = await this.getConfig();

    if (!getConfigResult.success) {
      return { success: false, error: getConfigResult.error };
    }

    const { config } = getConfigResult;

    await this.authenticate(config.loginUrl);

    const loadWidgetScriptResult = await this.loadWidgetScript(config);
    if (!loadWidgetScriptResult.success) {
      return { success: false, error: loadWidgetScriptResult.error };
    }

    const widgetElement = await this.setWidgetElement();

    await this.openWidgetElement(widgetElement);

    return { success: true };
  }

  async getConfig(): Promise<DeskproChatWidgetGetConfigResult> {
    return firstValueFrom(
      this.freelancerHttp
        .get<SupportChatConfigGetResponseAjaxApi>(
          `support-chat/supportChatConfig.php`,
          { isGaf: true },
        )
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status !== 'success') {
        const error = new Error('Failed to load support chat widget.');
        this.errorHandler.handleError(error);
        return { success: false, error };
      }

      return { success: true, config: response.result.config };
    });
  }

  /**
   * Login / register users to Deskpro via JWT SSO. Didn't use an iframe
   * since we don't need the content from the request. Deskpro has no
   * solution at the moment for applying SSO on the chat widget.
   * An on error will be triggered since the response status is 302 and not
   * 200.
   */
  async authenticate(loginUrl: string): Promise<void> {
    return new Promise(resolve => {
      const img = new Image();
      img.onerror = () => {
        resolve();
      };
      img.onload = () => {
        resolve();
      };
      img.setAttribute('style', 'display: none');
      img.src = loginUrl;
      img.id = WIDGET_AUTH_ELEMENT_ID;
      this.document.body.appendChild(img);
    });
  }

  async loadWidgetScript(
    config: SupportChatConfig,
  ): Promise<DeskproChatWidgetLoadWidgetScriptResult> {
    // Set Deskpro chat widget options.
    // See https://support.deskpro.com/en/kb/articles/overriding-widget-and-chat-settings
    window.DESKPRO_WIDGET_OPTIONS = {
      chat: {
        begin_mode: 'conversation',
        default_department: config.chatDepartment?.id,
        default_values: {
          name: config.name,
          email: config.email,
        },
        select_department: 'default',
        waiting_timeout: WAITING_TIMEOUT_IN_SECONDS,
      },
      helpdeskUrl: config.helpdeskUrl,
      jwt: config.authToken,
      ticket: {
        default_department: config.ticketDepartment?.id,
        select_department: 'default',
      },
    };

    return new Promise(resolve => {
      const loader = this.document.createElement('script');
      loader.onerror = () => {
        const error = new Error('Failed to load support chat widget.');
        this.errorHandler.handleError(error);
        resolve({ success: false, error });
      };
      loader.onload = () => resolve({ success: true });
      loader.src = config.widgetLoaderUrl;
      loader.id = WIDGET_SCRIPT_ID;
      this.document.head.appendChild(loader);
    });
  }

  async setWidgetElement(): Promise<DeskproChatWidgetElement> {
    return new Promise(resolve => {
      const onWidgetElementReady = (event: Event): void => {
        this.hideWidgetButton();

        resolve((event as DeskproChatWidgetReadyEvent).detail.DpWidget);
      };
      this.onWidgetElementReady = onWidgetElementReady.bind(this);

      window.addEventListener(WIDGET_READY_EVENT, this.onWidgetElementReady);
    });
  }

  async openWidgetElement(
    widgetElement: DeskproChatWidgetElement,
  ): Promise<void> {
    return new Promise(resolve => {
      widgetElement.openWidget();

      resolve();
    });
  }

  hideWidgetButton(): void {
    const widgetButton = this.document.querySelector(WIDGET_BUTTON_SELECTOR);
    widgetButton?.setAttribute('style', 'display: none');
  }

  clearElements(): void {
    const widgetAuthElement = this.document.getElementById(
      WIDGET_AUTH_ELEMENT_ID,
    );
    const widgetScript = this.document.getElementById(WIDGET_SCRIPT_ID);
    const widgetContainer = this.document.querySelector(
      WIDGET_CONTAINER_SELECTOR,
    );

    const widgetLoader = this.document.querySelector(WIDGET_LOADER_SELECTOR);

    widgetAuthElement?.parentNode?.removeChild(widgetAuthElement);
    widgetScript?.parentNode?.removeChild(widgetScript);
    widgetContainer?.parentNode?.removeChild(widgetContainer);
    widgetLoader?.parentNode?.removeChild(widgetLoader);

    if (this.onWidgetElementReady) {
      window.removeEventListener(WIDGET_READY_EVENT, this.onWidgetElementReady);
    }
  }
}
